import React from 'react';
import { VOICE_ACTIVE_COLOR, VOICE_BARS } from './FooterVoice.constants';

const clampProgress = (value) => Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));

const VoiceWaveform = ({
    progress = 0,
    isDarkMode,
    isPlaying = false,
    bars = VOICE_BARS,
    activeColor = VOICE_ACTIVE_COLOR,
    inactiveColor,
    onSeek,
    ariaLabel = 'Seek audio',
    width,
    flex = 1,
}) => {
    const safeProgress = clampProgress(progress);
    const waveformBars = bars.length ? bars : VOICE_BARS;
    const playedBars = (safeProgress / 100) * waveformBars.length;
    const canSeek = typeof onSeek === 'function';
    const mutedBar = inactiveColor || (isDarkMode ? 'rgba(255, 255, 255, 0.22)' : '#d9e1ea');

    const handleSeek = (event) => {
        if (!canSeek) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (!rect.width) return;
        const nextProgress = ((event.clientX - rect.left) / rect.width) * 100;
        onSeek(clampProgress(nextProgress));
    };

    const handleKeyDown = (event) => {
        if (!canSeek) return;

        const step = event.shiftKey ? 10 : 5;
        const keyProgress = {
            ArrowLeft: safeProgress - step,
            ArrowDown: safeProgress - step,
            ArrowRight: safeProgress + step,
            ArrowUp: safeProgress + step,
            Home: 0,
            End: 100,
        }[event.key];

        if (keyProgress === undefined) return;
        event.preventDefault();
        onSeek(clampProgress(keyProgress));
    };

    return (
        <div
            {...(canSeek ? {
                role: 'slider',
                tabIndex: 0,
                'aria-label': ariaLabel,
                'aria-valuemin': 0,
                'aria-valuemax': 100,
                'aria-valuenow': Math.round(safeProgress),
            } : {
                'aria-hidden': true,
            })}
            onClick={handleSeek}
            onKeyDown={handleKeyDown}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                height: 32,
                minWidth: 0,
                width,
                flex,
                cursor: canSeek ? 'pointer' : 'default'
            }}
        >
            {waveformBars.map((height, index) => {
                const isActive = playedBars > index;
                const isNearPlayhead = isPlaying && Math.abs(index - playedBars) <= 1;
                return (
                    <div
                        key={index}
                        style={{
                            width: 2,
                            flex: '1 1 2px',
                            maxWidth: 3,
                            minWidth: 1,
                            height: `${height}%`,
                            borderRadius: 4,
                            background: (isActive || isNearPlayhead) ? activeColor : mutedBar,
                            opacity: isPlaying && !isActive && !isNearPlayhead ? 0.82 : 1,
                            transition: 'background 0.16s ease, opacity 0.16s ease'
                        }}
                    />
                );
            })}
        </div>
    );
};

export default VoiceWaveform;
